import React, { forwardRef, useLayoutEffect, useRef } from "react";
import { useThree, type ThreeElements } from "@react-three/fiber";
import * as THREE from "three";
import { clampFillOpacity } from "../../utils/fillOpacity";
import { setObjectMaterialOpacity } from "../../utils/objectMaterialOpacity";

type ObjectOpacityGroupProps = Omit<ThreeElements["group"], "ref"> & {
  /** Object fill opacity from 0 (transparent) to 1 (opaque). */
  opacity?: number;
};

/**
 * Applies a uniform fill opacity to every material under this group.
 *
 * Door/window/furniture strokes often use drei `Line`, which does not always
 * pick up live opacity prop updates. Traversing materials matches the
 * remount/refresh appearance without rebuilding geometry each slider tick.
 */
const ObjectOpacityGroup = forwardRef<THREE.Group, ObjectOpacityGroupProps>(
  function ObjectOpacityGroup({ opacity = 1, children, ...props }, ref) {
    const localRef = useRef<THREE.Group | null>(null);
    const { invalidate } = useThree();
    const clampedOpacity = clampFillOpacity(opacity);

    const setRefs = (node: THREE.Group | null) => {
      localRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    useLayoutEffect(() => {
      const root = localRef.current;
      if (!root) return;

      const apply = () => {
        setObjectMaterialOpacity(root, clampedOpacity);
        invalidate();
      };

      apply();
      // Line2 materials from drei can attach one frame after the group mounts.
      const frame = requestAnimationFrame(apply);
      return () => cancelAnimationFrame(frame);
    }, [clampedOpacity, invalidate]);

    return (
      <group ref={setRefs} {...props}>
        {children}
      </group>
    );
  },
);

export default ObjectOpacityGroup;
